import React, { useState, useEffect } from 'react';
import { StatusBadge, PriorityBadge } from './mobile/Badges';
import { ApprovalModal } from './ApprovalModal';
import type { Demand, DemandComment } from '../types';
import { demandsAPI, rulesAPI, commentsAPI, usersAPI, attachmentsAPI, messagesAPI, crmAPI } from '../api';
import { useAlert } from '../contexts/AlertContext';
import { 
  Landmark, 
  FileText, 
  ChevronLeft, 
  Send, 
  EyeOff, 
  ShieldAlert, 
  Star,
  Printer,
  Sparkles,
  FileSpreadsheet,
  Trash2,
  Download,
  Clock,
  Loader2,
  Zap,
  X,
  CheckCircle2,
  Mail,
  Smartphone,
  Eye
} from 'lucide-react';

interface DemandDetailProps {
  demandId: string;
  initialEditMode?: boolean;
  userRole: string;
  userPermissions: string[];
  onBack: () => void;
}

export const DemandDetail: React.FC<DemandDetailProps> = ({ 
  demandId, 
  initialEditMode = false,
  userPermissions,
  onBack 
}) => {
  const { showAlert, showConfirm } = useAlert();
  const [demand, setDemand] = useState<Demand | null>(null);
  const [municipalities, setMunicipalities] = useState<any[]>([]);
  const [entities, setEntities] = useState<any[]>([]);
  const [people, setPeople] = useState<any[]>([]);
  const [isResolveModalOpen, setIsResolveModalOpen] = useState(false);
  const [selectedResolveMunicipalityId, setSelectedResolveMunicipalityId] = useState('');
  const [selectedResolveEntityId, setSelectedResolveEntityId] = useState('');
  const [selectedResolvePersonId, setSelectedResolvePersonId] = useState('');
  const [selectedResolveAllocationId, setSelectedResolveAllocationId] = useState('');
  const [resolveReason, setResolveReason] = useState('');
  const [loading, setLoading] = useState(true);
  
  // Abas do painel operacional
  const [activeSubTab, setActiveSubTab] = useState<'details' | 'forwardings' | 'attachments' | 'documents' | 'comments' | 'timeline'>('details');

  // Listagem de usuários e comentários físicos
  const [usersList, setUsersList] = useState<any[]>([]);
  const [commentsList, setCommentsList] = useState<DemandComment[]>([]);
  const [messagesList, setMessagesList] = useState<any[]>([]);
  const [showAllCommunications, setShowAllCommunications] = useState(false);

  // Modo de Edição
  const [isEditMode, setIsEditMode] = useState(initialEditMode);
  const [autosave, setAutosave] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [saveIndicator, setSaveIndicator] = useState<'saved' | 'saving' | 'dirty'>('saved');

  // Campos do Formulário
  const [title, setTitle] = useState('');
  const [solicitanteName, setSolicitanteName] = useState('');
  const [city, setCity] = useState('');
  const [uf, setUf] = useState('SP');
  const [themeArea, setThemeArea] = useState('');
  const [cabinet, setCabinet] = useState<'STATE' | 'FEDERAL' | 'BOTH' | 'OTHER'>('STATE');
  const [sphere, setSphere] = useState<'ESTADUAL' | 'FEDERAL' | 'MUNICIPAL' | 'OUTRO'>('ESTADUAL');
  const [valueRequested, setValueRequested] = useState('');
  const [valueApproved, setValueApproved] = useState('');
  const [priority, setPriority] = useState<'BAIXA' | 'MEDIA' | 'ALTA' | 'URGENTE'>('MEDIA');
  const [status, setStatus] = useState<any>('RECEBIDA');
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const [isResendModalOpen, setIsResendModalOpen] = useState(false);
  const [resendDocId, setResendDocId] = useState('');
  const [resendPhone, setResendPhone] = useState('');
  const [summary, setSummary] = useState('');
  const [forwardingSuggestion, setForwardingSuggestion] = useState('');
  const [assignedUserId, setAssignedUserId] = useState('');

  // Campos do Contato associado
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactCpfCnpj, setContactCpfCnpj] = useState('');
  const [contactResponsible, setContactResponsible] = useState('');

  // Document Viewer State
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [docError, setDocError] = useState(false);

  const attachment = demand?.attachments?.[0];
  const isImage = attachment?.fileType?.includes('image') ||
    !!attachment?.fileName?.match(/\.(jpg|jpeg|png)$/i);

  useEffect(() => {
    if (!attachment?.fileUrl) return;
    setLoadingDoc(true);
    setDocError(false);
    let objectUrl: string | null = null;

    attachmentsAPI.fetchBlob(attachment.fileUrl)
      .then(result => {
        if (result) {
          objectUrl = URL.createObjectURL(result.blob);
          setBlobUrl(objectUrl);
        } else {
          setDocError(true);
        }
      })
      .catch(() => setDocError(true))
      .finally(() => setLoadingDoc(false));

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment?.fileUrl]);


  // Novo comentário equipe
  const [newCommentText, setNewCommentText] = useState('');

  // Anexos
  const [newAttachmentName, setNewAttachmentName] = useState('');
  const [newAttachmentFile, setNewAttachmentFile] = useState<File | null>(null);

  // Encaminhamentos
  const [forwardingAgency, setForwardingAgency] = useState('');
  const [forwardingResponsible, setForwardingResponsible] = useState('');
  const [forwardingNotes, setForwardingNotes] = useState('');

  // Modal IA Corrigir
  const [isAICorrectOpen, setIsAICorrectOpen] = useState(false);
  const [aiCorrectTerm, setAiCorrectTerm] = useState('');
  const [aiCorrectArea, setAiCorrectArea] = useState('Saúde');

  // Modal Reprocessar IA
  const [isReprocessOpen, setIsReprocessOpen] = useState(false);
  const [reprocessMode, setReprocessMode] = useState<'replace' | 'suggest'>('suggest');
  const [aiSuggestions, setAiSuggestions] = useState<any>(null);

  const loadDemand = async () => {
    setLoading(true);
    try {
      const data = await demandsAPI.getById(demandId);
      setDemand(data);
      
      // Inicializar campos de formulário
      setTitle(data.title || '');
      setSolicitanteName(data.solicitanteName || '');
      setCity(data.city || '');
      setUf(data.uf || 'SP');
      setThemeArea(data.themeArea || '');
      setCabinet(data.cabinet || 'STATE');
      setSphere(data.sphere as any || 'ESTADUAL');
      setValueRequested(String(data.valueRequested || ''));
      setValueApproved(String(data.valueApproved || ''));
      setPriority(data.priority || 'MEDIA');
      setStatus(data.status || 'RECEBIDA');
      setSummary(data.summary || '');
      setForwardingSuggestion(data.forwardingSuggestion || '');
      setAssignedUserId(data.assignedUser?.id || '');

      if (data.contact) {
        setContactName(data.contact.nameEntity || '');
        setContactPhone(data.contact.phone || '');
        setContactEmail(data.contact.email || '');
        setContactCpfCnpj(data.contact.cpfCnpj || '');
        setContactResponsible(data.contact.responsible || '');
      }

      // Buscar comentários reais
      const comments = await commentsAPI.list(demandId);
      setCommentsList(comments);

      // Buscar histórico de comunicações
      const messages = await messagesAPI.list(demandId);
      setMessagesList(messages);

    } catch (e) {
      showAlert('error', 'Erro', 'Erro ao carregar detalhes da demanda.');
    } finally {
      setLoading(false);
    }

    // Buscar assessores do gabinete (separado — erro aqui não bloqueia a demanda)
    try {
      const users = await usersAPI.list();
      setUsersList(users);
    } catch (_) {
      // Silencioso: lista de usuários é opcional para exibir a demanda
    }
  };

  useEffect(() => {
    loadDemand();
    crmAPI.listMunicipalities().then(setMunicipalities).catch(console.error);
    crmAPI.listEntities().then(setEntities).catch(console.error);
    crmAPI.listPeople().then(setPeople).catch(console.error);
  }, [demandId]);

  useEffect(() => {
    if (demand) {
      setSelectedResolveMunicipalityId(demand.crmMunicipalityId || '');
      setSelectedResolveEntityId(demand.crmEntityId || '');
      setSelectedResolvePersonId(demand.crmPersonId || '');
      setSelectedResolveAllocationId(demand.crmAllocationId || '');
    }
  }, [demand, isResolveModalOpen]);

  const handleSaveResolveRelationship = async () => {
    if (!demand) return;
    try {
      await crmAPI.resolveRelationship(demand.id, {
        action: 'CHANGED',
        municipalityId: selectedResolveMunicipalityId || undefined,
        entityId: selectedResolveEntityId || undefined,
        personId: selectedResolvePersonId || undefined,
        allocationId: selectedResolveAllocationId || undefined,
        reason: resolveReason || 'Vínculos ajustados manualmente pelo assessor.'
      });
      showAlert('success', 'Sucesso', 'Relacionamento com o CRM ajustado com sucesso.');
      setIsResolveModalOpen(false);
      setResolveReason('');
      loadDemand();
    } catch (err: any) {
      showAlert('error', 'Erro', 'Erro ao salvar alteração de relacionamento: ' + err.message);
    }
  };

  const [resendEmailAddress, setResendEmailAddress] = useState('');
  const [resendChannels, setResendChannels] = useState<{ whatsapp: boolean; email: boolean }>({ whatsapp: true, email: false });

  const handleConfirmResend = () => {
    if (!demand) return;
    const cleanPhone = resendPhone.replace(/[^\d]/g, '');
    
    if (resendChannels.whatsapp && !cleanPhone) {
      showAlert('error', 'Erro', 'Por favor, informe um número de telefone válido.');
      return;
    }
    if (resendChannels.email && !resendEmailAddress.trim()) {
      showAlert('error', 'Erro', 'Por favor, informe um endereço de e-mail válido.');
      return;
    }

    demandsAPI.deliverDocument(demand.id, resendDocId, {
      sendWhatsApp: resendChannels.whatsapp,
      sendEmail: resendChannels.email,
      whatsappNumbers: resendChannels.whatsapp ? [cleanPhone] : [],
      emailAddresses: resendChannels.email ? [resendEmailAddress.trim()] : []
    })
      .then(() => {
        showAlert('success', 'Sucesso', 'Reenvio de documento agendado na fila com sucesso!');
        setIsResendModalOpen(false);
      })
      .catch((err: any) => showAlert('error', 'Erro', 'Erro ao reenviar: ' + err.message));
  };

  // Monitorar alterações para Autosave e Dirty Indicator
  useEffect(() => {
    if (!demand || !isEditMode) return;
    setIsDirty(true);
    setSaveIndicator('dirty');
  }, [
    title, solicitanteName, city, uf, themeArea, cabinet, sphere, 
    valueRequested, valueApproved, priority, status, summary, 
    assignedUserId, contactName, contactPhone, contactEmail, 
    contactCpfCnpj, contactResponsible
  ]);

  // Autosave Effect
  useEffect(() => {
    if (!autosave || !isDirty || !demand) return;
    const timer = setTimeout(() => {
      handleSave();
    }, 4000);
    return () => clearTimeout(timer);
  }, [isDirty, autosave]);

  const handleApproval = async (data: any) => {
    if (!demand) return;
    try {
      await demandsAPI.approveAndNotify(demand.id, data);
      showAlert('success', 'Sucesso', 'Demanda aprovada com sucesso!');
      setIsApprovalModalOpen(false);
      loadDemand(); // Reload demand data
    } catch (err: any) {
      showAlert('error', 'Erro', 'Erro ao aprovar demanda: ' + (err.response?.data?.error || err.message));
    }
  };

  // Função Salvar Alterações
  const handleSave = async () => {
    if (!demand) return;
    setSaveIndicator('saving');
    try {
      const updatedData = {
        title,
        solicitanteName,
        city,
        uf,
        themeArea,
        cabinet,
        sphere,
        valueRequested: valueRequested === '' ? null : Number(valueRequested),
        valueApproved: valueApproved === '' ? null : Number(valueApproved),
        priority,
        status,
        summary,
        assignedUserId,
        contactName,
        contactPhone,
        contactEmail,
        contactCpfCnpj,
        contactResponsible,
        isFavorite: demand.isFavorite
      };

      await demandsAPI.update(demand.id, updatedData);
      setIsDirty(false);
      setSaveIndicator('saved');
      
      // Atualizar localmente a timeline / auditoria
      const reloaded = await demandsAPI.getById(demand.id);
      setDemand(reloaded);
    } catch (e) {
      setSaveIndicator('dirty');
      showAlert('error', 'Erro', 'Erro ao salvar alterações.');
    }
  };

  // Favoritar
  const handleToggleFavorite = async () => {
    if (!demand) return;
    try {
      await demandsAPI.toggleFavorite(demand.id);
      setDemand({ ...demand, isFavorite: !demand.isFavorite });
    } catch (err) {
      showAlert('error', 'Erro', 'Erro ao favoritar.');
    }
  };

  // Adicionar Anexo
  const handleAddAttachment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!demand || !newAttachmentFile) return;
    try {
      await demandsAPI.uploadAttachment(demand.id, newAttachmentFile);
      setNewAttachmentName('');
      setNewAttachmentFile(null);
      const reloaded = await demandsAPI.getById(demand.id);
      setDemand(reloaded);
      showAlert('success', 'Sucesso', 'Anexo enviado com sucesso!');
    } catch (e) {
      showAlert('error', 'Erro', 'Erro ao salvar anexo.');
    }
  };

  // Download Anexo
  const handleDownloadAttachment = async (fileUrl: string, fileName: string) => {
    try {
      const res = await attachmentsAPI.fetchBlob(fileUrl);
      if (res) {
        const url = window.URL.createObjectURL(res.blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } else {
        showAlert('error', 'Erro', 'Erro ao baixar anexo (vazio);.');
      }
    } catch (err) {
      showAlert('error', 'Erro', 'Erro ao fazer download do arquivo.');
    }
  };

  // Remover Anexo
  const handleRemoveAttachment = async (attId: string) => {
    if (!demand) return;
    showConfirm('Excluir Anexo', 'Deseja realmente remover este anexo?', async () => {
      try {
        await demandsAPI.removeAttachment(demand.id, attId);
        const reloaded = await demandsAPI.getById(demand.id);
        setDemand(reloaded);
      } catch (e) {
        showAlert('error', 'Erro', 'Erro ao remover anexo.');
      }
    }, true);
  };

  // Adicionar Comentário de Equipe (Discussão Banco Físico)
  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!demand || !newCommentText.trim()) return;

    try {
      await commentsAPI.create(demand.id, newCommentText);
      setNewCommentText('');
      
      const comments = await commentsAPI.list(demand.id);
      setCommentsList(comments);
    } catch (err) {
      showAlert('error', 'Erro', 'Erro ao postar comentário.');
    }
  };

  // Criar Regra de Correção IA (Treinamento)
  const handleSaveAICorrection = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!demand || !aiCorrectTerm || !aiCorrectArea) return;
    try {
      await rulesAPI.create(aiCorrectTerm, aiCorrectArea);
      setIsAICorrectOpen(false);
      setAiCorrectTerm('');
      showAlert('info', 'Aviso', `Regra de treinamento criada! A IA agora mapeará o termo "${aiCorrectTerm}" para a área de "${aiCorrectArea}".`);
    } catch (e) {
      showAlert('error', 'Erro', 'Erro ao salvar regra.');
    }
  };

  // Reprocessar com IA
  const handleReprocessIA = async () => {
    if (!demand) return;
    try {
      const res = await demandsAPI.reprocess(demand.id, reprocessMode);
      if (reprocessMode === 'replace') {
        const reloaded = await demandsAPI.getById(demand.id);
        setDemand(reloaded);
        setIsReprocessOpen(false);
        showAlert('success', 'Sucesso', 'Demanda reprocessada e dados substituídos com sucesso!');
      } else {
        setAiSuggestions(res.suggestions);
      }
    } catch (e) {
      showAlert('error', 'Erro', 'Erro ao reprocessar.');
    }
  };

  // Aplicar sugestão campo a campo
  const acceptSuggestion = (field: string, val: any) => {
    if (field === 'title') setTitle(val);
    if (field === 'solicitanteName') setSolicitanteName(val);
    if (field === 'valueRequested') setValueRequested(val);
    if (field === 'summary') setSummary(val);
    if (field === 'forwardingSuggestion') setForwardingSuggestion(val);
    // Limpar sugestão da tela
    if (aiSuggestions) {
      const copy = { ...aiSuggestions };
      delete copy[field];
      setAiSuggestions(Object.keys(copy).length === 0 ? null : copy);
    }
  };

  // Adicionar Encaminhamento
  const handleAddForwarding = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!demand || !forwardingAgency) return;
    try {
      const notes = `Órgão: ${forwardingAgency} | Responsável: ${forwardingResponsible || 'Não especificado'} | Obs: ${forwardingNotes || 'Nenhuma'}`;
      await demandsAPI.updateStatus(demand.id, 'ENCAMINHADA', notes);
      
      setForwardingAgency('');
      setForwardingResponsible('');
      setForwardingNotes('');
      
      const reloaded = await demandsAPI.getById(demand.id);
      setDemand(reloaded);
      setStatus('ENCAMINHADA');
      showAlert('info', 'Aviso', 'Encaminhamento registrado e timeline atualizada!');
    } catch (e) {
      showAlert('error', 'Erro', 'Erro ao registrar encaminhamento.');
    }
  };

  // Impressão A4
  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="py-24 text-center text-xs font-semibold text-slate-500 font-sans">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500 mx-auto mb-2" />
        Carregando painel operacional da demanda...
      </div>
    );
  }

  if (!demand) {
    return (
      <div className="py-24 text-center text-xs text-red-500 font-bold font-sans">
        Erro ao carregar dados desta demanda.
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      'RECEBIDA': 'text-blue-500 bg-blue-500/10 border-blue-500/20',
      'EM_TRIAGEM': 'text-amber-500 bg-amber-500/10 border-amber-500/20',
      'EM_ANALISE': 'text-purple-500 bg-purple-500/10 border-purple-500/20',
      'APROVADA': 'text-emerald-600 bg-emerald-500/15 border-emerald-500/30',
      'CONCLUIDA': 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20',
      'ARQUIVADA': 'text-slate-550 bg-slate-500/10 border-slate-500/20',
    };
    return colors[status] || 'text-slate-500 bg-slate-500/10';
  };

  const getPriorityStyle = (p: string) => {
    const styles: Record<string, string> = {
      'BAIXA': 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-350',
      'MEDIA': 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-400',
      'ALTA': 'bg-amber-100 text-amber-850 dark:bg-amber-950 dark:text-amber-400',
      'URGENTE': 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-400 animate-pulse font-extrabold',
    };
    return styles[p] || 'bg-slate-100 text-slate-700';
  };

  const commentsCount = commentsList.length;

  const FIELD_MAP: Record<string, string> = {
    isFavorite: 'Favorito',
    valueRequested: 'Valor Solicitado',
    valueApproved: 'Valor Aprovado',
    assignedUserId: 'Responsável Atribuído',
    status: 'Status',
    priority: 'Prioridade',
    title: 'Título',
    description: 'Descrição',
    internalNotes: 'Notas Internas',
    city: 'Cidade',
    themeArea: 'Área Temática',
    cabinet: 'Gabinete',
    solicitanteName: 'Solicitante',
  };

  const ACTION_MAP: Record<string, string> = {
    CREATE: 'Criação',
    UPDATE: 'Edição',
    DELETE: 'Exclusão',
    VIEW: 'Visualização',
    STATUS_CHANGE: 'Alteração de Status',
    ATTACHMENT_UPLOAD: 'Upload de Anexo',
    AI_REPROCESS: 'Reprocessamento IA',
  };

  // Unificar Timeline (Mudanças de Status + Auditoria)
  const unifiedTimelineEvents = [
    ...(demand.statusHistories || []).map((sh: any) => ({
      id: sh.id,
      type: 'status',
      title: `Status alterado para ${sh.statusTo.replace('_', ' ')}`,
      description: sh.notes || 'Sem observações.',
      date: sh.createdAt,
      user: sh.changedBy?.name || 'Assessor'
    })),
    ...(demand.auditLogs || []).map((al: any) => {
      const actionName = ACTION_MAP[al.action] || al.action;
      let desc = 'Ação registrada no sistema.';
      
      if (al.payload?.fieldsChanged) {
        desc = `Campos modificados: ${al.payload.fieldsChanged.map((c: any) => {
          const fieldName = FIELD_MAP[c.field] || c.field;
          const beforeStr = c.before === null || c.before === undefined || c.before === '' ? 'Vazio' : String(c.before);
          const afterStr = c.after === null || c.after === undefined || c.after === '' ? 'Vazio' : String(c.after);
          return `${fieldName}: De "${beforeStr}" para "${afterStr}"`;
        }).join(' | ')}`;
      } else if (al.payload?.action === 'add_attachment') {
        desc = `Anexo adicionado: ${al.payload.fileName}`;
      } else if (al.payload?.action === 'remove_attachment') {
        desc = `Anexo removido: ${al.payload.fileName}`;
      } else if (al.action === 'VIEW') {
        desc = `Visualizou a demanda.`;
      }

      return {
        id: al.id,
        type: 'audit',
        title: actionName,
        description: desc,
        date: al.createdAt,
        user: al.user?.name || 'Sistema'
      };
    }),
    {
      id: 'creation-event',
      type: 'creation',
      title: 'Demanda Criada',
      description: `Entrada via canal ${demand.source.toLowerCase()}. Extração estruturada via OpenAI Vision.`,
      date: demand.createdAt,
      user: 'IA Parser Engine'
    }
  ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  return (
    <>

    {/* MOBILE: Clean Header */}
    {demand && (
      <div className="md:hidden pt-4 px-4 pb-2 bg-slate-50 dark:bg-slate-950">
        <div className="font-mono text-[11px] font-bold text-slate-500 mb-1">{demand.protocol}</div>
        <div className="flex items-center gap-1.5 mb-2">
          <PriorityBadge priority={demand.priority} size="xs" />
          <StatusBadge status={demand.status} size="xs" />
        </div>
        <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 leading-snug w-full tracking-tight">
          {title || demand.title}
        </h1>
      </div>
    )}

    <div className="space-y-6 text-left print:bg-white print:text-black px-4 md:px-0 pb-36 md:pb-0">
      
      {/* 1. TOP HEADER & GENERAL ACTIONS */}
      <div className="hidden md:flex flex-col md:flex-row md:items-center justify-between gap-4 print:hidden">
        <div>
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 text-xs font-bold transition-all mb-3 focus:outline-none"
          >
            <ChevronLeft className="w-4 h-4" />
            Voltar para Lista
          </button>

          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-400 font-mono tracking-wider font-bold bg-slate-100 dark:bg-slate-900 px-2 py-0.5 rounded">
              {demand.protocol}
            </span>
            <h1 className="text-xl font-bold tracking-tight text-slate-800 dark:text-slate-100 font-sans leading-tight">
              {title || demand.title}
            </h1>
          </div>
        </div>

        {/* Ações Cabeçalho */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Botão Aprovar Demanda */}
          {userPermissions.includes('demand:edit') && demand.status !== 'APROVADA' && (
            <button
              onClick={() => setIsApprovalModalOpen(true)}
              className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs rounded-xl shadow-md shadow-emerald-500/10 transition-all"
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              Aprovar Demanda
            </button>
          )}

          {/* Estrela Favorito */}
          <button
            onClick={handleToggleFavorite}
            className="p-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-900 dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-800 text-slate-450 hover:text-amber-500 rounded-xl transition-all"
            title="Favoritar Demanda"
          >
            <Star className={`w-4 h-4 ${demand.isFavorite ? 'fill-amber-500 text-amber-500' : ''}`} />
          </button>

          {/* Reprocessar com IA */}
          {userPermissions.includes('demand:edit') && (
            <button
              onClick={() => setIsReprocessOpen(true)}
              className="flex items-center gap-1 px-3 py-2 bg-purple-650 hover:bg-purple-550 text-white font-bold text-xs rounded-xl shadow-md shadow-purple-500/10 transition-all"
            >
              <Sparkles className="w-3.5 h-3.5" />
              Reprocessar com IA
            </button>
          )}

          {/* Imprimir */}
          <button
            onClick={handlePrint}
            className="flex items-center gap-1.5 px-3 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-900 dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 font-bold text-xs rounded-xl transition-all"
          >
            <Printer className="w-3.5 h-3.5" />
            Imprimir Resumo
          </button>

          {/* Editar Toggle */}
          {userPermissions.includes('demand:edit') && (
            <button
              onClick={() => {
                if (isEditMode && isDirty) {
                  handleSave();
                }
                setIsEditMode(!isEditMode);
              }}
              className={`px-4 py-2 text-xs font-bold rounded-xl transition-all shadow-md focus:outline-none ${
                isEditMode
                  ? 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-emerald-550/10'
                  : 'bg-blue-600 hover:bg-blue-500 text-white shadow-blue-550/10'
              }`}
            >
              {isEditMode ? 'Salvar Edição' : 'Editar Demanda'}
            </button>
          )}
        </div>
      </div>

      {/* 2. PRINT ONLY AREA (Ocultada em modo tela, aparece na impressão A4) */}
      <div className="hidden print:block text-black bg-white p-8 space-y-6 font-serif max-w-[210mm] min-h-[297mm] mx-auto text-xs leading-relaxed">
        <div className="text-center border-b pb-4 mb-6">
          <h1 className="text-lg font-bold">GABINETE COMPARTILHADO DE DEMANDAS</h1>
          <p className="text-[10px] text-slate-500">Dani Alonso (Estadual) & Capitão Augusto (Federal)</p>
          <div className="mt-4 font-mono font-bold text-sm">PROTOCOLO: {demand.protocol}</div>
        </div>

        <div className="grid grid-cols-2 gap-4 pb-4 border-b">
          <div><strong>Solicitante:</strong> {solicitanteName || demand.solicitanteName}</div>
          <div><strong>Cidade/UF:</strong> {city || demand.city}/{uf || demand.uf}</div>
          <div><strong>Área Temática:</strong> {themeArea || demand.themeArea}</div>
          <div><strong>Status:</strong> {status.replace('_', ' ')}</div>
          <div><strong>Prioridade:</strong> {priority}</div>
          <div><strong>Data Recebimento:</strong> {new Date(demand.receivedAt).toLocaleDateString('pt-BR')}</div>
        </div>

        <div className="space-y-2">
          <h3 className="font-bold border-b pb-1">Resumo Executivo da Demanda</h3>
          <p className="text-justify">{summary || demand.summary}</p>
        </div>

        {forwardingSuggestion && (
          <div className="space-y-2">
            <h3 className="font-bold border-b pb-1">Sugestão de Encaminhamento da IA</h3>
            <p className="italic text-slate-700">{forwardingSuggestion}</p>
          </div>
        )}

        <div className="space-y-2">
          <h3 className="font-bold border-b pb-1">Tratativas e Encaminhamentos</h3>
          <ul className="list-disc list-inside space-y-1">
            {(demand.statusHistories || []).map(sh => (
              <li key={sh.id}>
                <strong>{new Date(sh.createdAt).toLocaleDateString('pt-BR')}:</strong> {sh.notes}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* 3. WORKSPACE LADO A LADO */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start print:hidden">
        
        {/* LADO ESQUERDO: REPRESENTAÇÃO DE OFÍCIO TIMBRADO (A4) */}
        <div className="lg:col-span-5 space-y-4">
          <div className="text-slate-450 uppercase font-bold text-[9px] tracking-wider flex items-center gap-1.5 px-2">
            <FileText className="w-3.5 h-3.5 text-blue-500" />
            Documento de Entrada Original (Simulado)
          </div>

          <div className="bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xl rounded-3xl h-[560px] overflow-hidden relative flex flex-col justify-center items-center">
            {loadingDoc ? (
              <div className="flex flex-col items-center gap-3 text-slate-400">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                <p className="text-sm">Carregando documento original...</p>
              </div>
            ) : docError ? (
              <div className="flex flex-col items-center gap-3 text-amber-500 p-8 text-center bg-amber-500/5 rounded-2xl border border-amber-500/10">
                <ShieldAlert className="w-10 h-10" />
                <p className="text-sm font-semibold text-amber-600 dark:text-amber-500">Documento não disponível</p>
                <p className="text-xs text-amber-600/70 dark:text-amber-500/70">O arquivo pode não ter sido salvo neste servidor ou o link expirou.</p>
              </div>
            ) : !attachment ? (
              <div className="flex flex-col items-center gap-3 text-slate-400 p-8 text-center">
                <FileText className="w-10 h-10 opacity-30" />
                <p className="text-sm">Nenhum documento anexado</p>
                <p className="text-xs opacity-60">Esta demanda foi criada por texto puro ou manualmente.</p>
              </div>
            ) : blobUrl ? (
              isImage ? (
                <img src={blobUrl} alt={attachment.fileName} className="max-w-full max-h-full object-contain" />
              ) : (
                <iframe src={blobUrl} title={attachment.fileName} className="w-full h-full border-0 bg-white" />
              )
            ) : null}
          </div>
        </div>

        {/* LADO DIREITO: CENTRO OPERACIONAL DE METADADOS E HISTÓRICOS */}
        <div className="lg:col-span-7 space-y-4">
          
          {/* Dirty Indicator / Autosave Switch */}
          {isEditMode && (
            <div className="glass-panel px-4 py-2.5 rounded-2xl flex items-center justify-between border-slate-200 dark:border-slate-800 text-xs">
              <div className="flex items-center gap-2">
                <span className={`w-2.5 h-2.5 rounded-full ${
                  saveIndicator === 'saved' ? 'bg-emerald-500' : (saveIndicator === 'saving' ? 'bg-blue-500 animate-spin border border-dashed border-white' : 'bg-amber-500 animate-pulse')
                }`}></span>
                <span className="font-bold text-slate-600 dark:text-slate-400">
                  {saveIndicator === 'saved' ? 'Alterações salvas no banco.' : (saveIndicator === 'saving' ? 'Salvando dados no Postgres...' : 'Há alterações pendentes.')}
                </span>
              </div>
              
              <label className="flex items-center gap-1.5 cursor-pointer font-semibold text-slate-500">
                <input
                  type="checkbox"
                  checked={autosave}
                  onChange={(e) => setAutosave(e.target.checked)}
                  className="rounded bg-slate-100 dark:bg-slate-900 border-slate-300"
                />
                Salvar Automaticamente (Autosave)
              </label>
            </div>
          )}

          {/* Abas Secundárias */}
          <div className="relative">
            <div className="flex overflow-x-auto scrollbar-hide border-b border-slate-200 dark:border-slate-800/80 text-xs font-semibold">
              {[
                { id: 'details', label: 'Metadados IA' },
                { id: 'forwardings', label: 'Encaminhamentos' },
                { id: 'documents', label: 'Documentos' },
                { id: 'attachments', label: 'Anexos' },
                { id: 'comments', label: 'Comentários', badge: commentsCount },
                { id: 'timeline', label: 'Timeline' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveSubTab(tab.id as any)}
                  className={`shrink-0 whitespace-nowrap py-2.5 px-3 sm:px-4 border-b-2 transition-all relative ${
                    activeSubTab === tab.id 
                      ? 'border-blue-600 text-blue-600 dark:text-blue-400 font-bold' 
                      : 'border-transparent text-slate-450 hover:text-slate-650'
                  }`}
                >
                  {tab.label}
                  {tab.badge !== undefined && tab.badge > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 text-[9px] font-bold">
                      {tab.badge}
                    </span>
                  )}
                </button>
              ))}
            </div>
            {/* Gradient fade to indicate more tabs on right */}
            <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white dark:from-slate-900 to-transparent md:hidden" />
          </div>

          {/* ABA: DETALHES DE METADADOS */}
          {activeSubTab === 'details' && (
            <div className="glass-panel p-6 rounded-3xl space-y-4 border border-slate-200 dark:border-slate-800 text-xs text-left animate-in fade-in duration-200">
              
              {/* SUGGESTION WIDGET (CRM INTEL - FASE 5) */}
              {demand.crmSuggestionStatus === 'PENDING' && (demand.crmMunicipalityId || demand.crmEntityId || demand.crmPersonId) ? (
                <div className="p-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/25 rounded-2xl space-y-3 mb-4">
                  <div className="flex items-center gap-2 text-blue-500 dark:text-blue-400 font-bold font-mono">
                    <Sparkles className="w-4 h-4 animate-pulse" />
                    <span>SUGESTÃO DE VÍNCULO INSTITUCIONAL (IA)</span>
                    <span className={`ml-auto px-2 py-0.5 rounded-full text-[9px] font-bold ${
                      demand.crmSuggestionConfidence === 'HIGH' ? 'bg-emerald-500/20 text-emerald-400' :
                      demand.crmSuggestionConfidence === 'MEDIUM' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-slate-500/20 text-slate-400'
                    }`}>
                      Confiança: {demand.crmSuggestionConfidence || 'MEDIUM'}
                    </span>
                  </div>
                  
                  <p className="text-slate-600 dark:text-slate-350 leading-relaxed font-sans text-[11px]">
                    <strong>Motivo:</strong> {demand.crmSuggestionReason || 'A IA detectou possíveis vínculos institucionais no documento de entrada.'}
                  </p>

                  <div className="space-y-1.5 text-[11px] bg-slate-900/10 dark:bg-slate-950/30 p-3 rounded-xl border border-slate-200/20">
                    {demand.crmMunicipalityId && (
                      <div>
                        <span className="text-slate-400 font-mono">Município Sugerido:</span>{' '}
                        <span className="font-semibold text-slate-850 dark:text-slate-200">
                          {municipalities.find(m => m.id === demand.crmMunicipalityId)?.name || 'Carregando...'}
                        </span>
                      </div>
                    )}
                    {demand.crmEntityId && (
                      <div>
                        <span className="text-slate-400 font-mono">Entidade Sugerida:</span>{' '}
                        <span className="font-semibold text-slate-850 dark:text-slate-200">
                          {entities.find(e => e.id === demand.crmEntityId)?.name || 'Carregando...'}
                        </span>
                      </div>
                    )}
                    {demand.crmPersonId && (
                      <div>
                        <span className="text-slate-400 font-mono">Pessoa/Prefeito Sugerido:</span>{' '}
                        <span className="font-semibold text-slate-850 dark:text-slate-200">
                          {people.find(p => p.id === demand.crmPersonId)?.name || 'Carregando...'}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2 pt-1.5">
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await crmAPI.resolveRelationship(demand.id, {
                            action: 'CONFIRMED',
                            municipalityId: demand.crmMunicipalityId || undefined,
                            entityId: demand.crmEntityId || undefined,
                            personId: demand.crmPersonId || undefined,
                            allocationId: demand.crmAllocationId || undefined,
                            reason: 'Confirmado pelo assessor no painel de metadados.'
                          });
                          showAlert('success', 'Sucesso', 'Relacionamento com o CRM confirmado com sucesso.');
                          loadDemand();
                        } catch (err: any) {
                          showAlert('error', 'Erro', 'Erro ao confirmar relacionamento: ' + err.message);
                        }
                      }}
                      className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-lg transition-all text-[11px]"
                    >
                      Confirmar Vínculo
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsResolveModalOpen(true)}
                      className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-lg transition-all text-[11px]"
                    >
                      Trocar/Ajustar
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await crmAPI.resolveRelationship(demand.id, {
                            action: 'IGNORED',
                            reason: 'Ignorado pelo assessor.'
                          });
                          showAlert('info', 'Aviso', 'Sugestão ignorada.');
                          loadDemand();
                        } catch (err: any) {
                          showAlert('error', 'Erro', 'Erro ao ignorar sugestão.');
                        }
                      }}
                      className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-350 font-bold rounded-lg transition-all text-[11px]"
                    >
                      Ignorar
                    </button>
                  </div>
                </div>
              ) : (
                <div className="p-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl flex items-center justify-between mb-4 gap-3">
                  <div className="text-[11px]">
                    <span className="font-bold text-slate-750 dark:text-slate-200 block">Vínculo com o CRM</span>
                    <span className="text-slate-450">
                      {demand.crmSuggestionStatus === 'CONFIRMED' 
                        ? `Vinculado a: ${municipalities.find(m => m.id === demand.crmMunicipalityId)?.name || 'Carregando...'}`
                        : 'Esta demanda não está vinculada a nenhuma entidade/município do CRM.'}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsResolveModalOpen(true)}
                    className="px-3 py-1.5 bg-slate-200 hover:bg-slate-300 dark:bg-slate-800 dark:hover:bg-slate-750 font-bold rounded-lg transition-all text-[10px] shrink-0 text-slate-700 dark:text-slate-200"
                  >
                    Ajustar Vínculos
                  </button>
                </div>
              )}

              {/* Formulário Metadados */}
              <div className="space-y-4">
                
                {/* Campo: Título */}
                <div>
                  <label className="block text-slate-400 mb-1 font-semibold font-mono">Assunto / Título</label>
                  {isEditMode ? (
                    <input
                      type="text"
                      className="w-full bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-850 rounded-lg p-2.5 text-slate-850 dark:text-slate-100 font-semibold focus:outline-none"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                    />
                  ) : (
                    <span className="font-bold text-slate-800 dark:text-slate-200 text-sm block py-1">{title}</span>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {/* Campo: Gabinete */}
                  <div>
                    <label className="block text-slate-400 mb-1 font-semibold font-mono">Gabinete Destinatário</label>
                    {isEditMode ? (
                      <select
                        className="w-full bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-850 rounded-lg p-2.5 focus:outline-none text-slate-800 dark:text-slate-100 font-semibold"
                        value={cabinet}
                        onChange={(e) => setCabinet(e.target.value as any)}
                      >
                        <option value="STATE">Dani Alonso (Estadual)</option>
                        <option value="FEDERAL">Capitão Augusto (Federal)</option>
                        <option value="BOTH">Ambos</option>
                      </select>
                    ) : (
                      <span className="font-semibold block py-1">{cabinet === 'STATE' ? 'Dep. Dani Alonso' : (cabinet === 'FEDERAL' ? 'Cap. Augusto' : 'Ambos/Geral')}</span>
                    )}
                  </div>

                  {/* Campo: Responsável */}
                  <div>
                    <label className="block text-slate-400 mb-1 font-semibold font-mono">Responsável Atual</label>
                    {isEditMode ? (
                      <select
                        className="w-full bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-850 rounded-lg p-2.5 focus:outline-none font-semibold text-slate-800 dark:text-slate-100"
                        value={assignedUserId}
                        onChange={(e) => setAssignedUserId(e.target.value)}
                      >
                        <option value="">Nenhum</option>
                        {usersList.map(u => (
                          <option key={u.id} value={u.id}>{u.name}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="font-semibold block py-1">
                        {usersList.find(u => u.id === assignedUserId)?.name || 'Sem Responsável'}
                      </span>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  {/* Campo: Esfera */}
                  <div>
                    <label className="block text-slate-400 mb-1 font-semibold font-mono">Esfera</label>
                    {isEditMode ? (
                      <select
                        className="w-full bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-850 rounded-lg p-2.5 focus:outline-none text-slate-850 dark:text-slate-100"
                        value={sphere}
                        onChange={(e) => setSphere(e.target.value as any)}
                      >
                        <option value="ESTADUAL">Estadual</option>
                        <option value="FEDERAL">Federal</option>
                        <option value="MUNICIPAL">Municipal</option>
                        <option value="OUTRO">Outro</option>
                      </select>
                    ) : (
                      <span className="font-medium block py-1">{sphere}</span>
                    )}
                  </div>

                  {/* Campo: Cidade */}
                  <div>
                    <label className="block text-slate-400 mb-1 font-semibold font-mono">Cidade</label>
                    {isEditMode ? (
                      <input
                        type="text"
                        className="w-full bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-850 rounded-lg p-2.5 focus:outline-none text-slate-800 dark:text-slate-100"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                      />
                    ) : (
                      <span className="font-medium block py-1">{city} - {uf}</span>
                    )}
                  </div>

                  {/* Campo: Prioridade */}
                  <div>
                    <label className="block text-slate-400 mb-1 font-semibold font-mono">Prioridade</label>
                    {isEditMode ? (
                      <select
                        className="w-full bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-850 rounded-lg p-2.5 focus:outline-none text-slate-800 dark:text-slate-100"
                        value={priority}
                        onChange={(e) => setPriority(e.target.value as any)}
                      >
                        <option value="BAIXA">Baixa</option>
                        <option value="MEDIA">Média</option>
                        <option value="ALTA">Alta</option>
                        <option value="URGENTE">Urgente</option>
                      </select>
                    ) : (
                      <span className={`inline-flex px-2 py-0.5 rounded font-bold text-[9px] mt-1 ${getPriorityStyle(priority)}`}>{priority}</span>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {/* Campo: Área */}
                  <div>
                    <label className="block text-slate-400 mb-1 font-semibold font-mono">Área Temática</label>
                    {isEditMode ? (
                      <input
                        type="text"
                        className="w-full bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-850 rounded-lg p-2.5 focus:outline-none text-slate-850 dark:text-slate-100 font-semibold"
                        value={themeArea}
                        onChange={(e) => setThemeArea(e.target.value)}
                      />
                    ) : (
                      <span className="px-2 py-0.5 mt-1 inline-flex bg-slate-100 dark:bg-slate-800 rounded font-semibold text-blue-500 border border-slate-200/20">{themeArea}</span>
                    )}
                  </div>

                  {/* Campo: Status */}
                  <div>
                    <label className="block text-slate-400 mb-1 font-semibold font-mono">Status Tratativa</label>
                    {isEditMode ? (
                      <select
                        className="w-full bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-850 rounded-lg p-2.5 focus:outline-none text-slate-800 dark:text-slate-100"
                        value={status}
                        onChange={(e) => setStatus(e.target.value as any)}
                      >
                        <option value="RECEBIDA">Recebida</option>
                        <option value="EM_TRIAGEM">Em Triagem</option>
                        <option value="EM_ANALISE">Em Análise</option>
                        <option value="APROVADA">Aprovada</option>
                        <option value="CONCLUIDA">Concluída</option>
                        <option value="ARQUIVADA">Arquivada</option>
                      </select>
                    ) : (
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-[10px] font-bold border mt-1 ${getStatusColor(status)}`}>
                        {status.replace('_', ' ')}
                      </span>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {/* Valor Solicitado */}
                  <div>
                    <label className="block text-slate-400 mb-1 font-semibold font-mono">Valor Solicitado (R$)</label>
                    {isEditMode ? (
                      <input
                        type="number"
                        className="w-full bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-850 rounded-lg p-2.5 focus:outline-none text-slate-800 dark:text-slate-100"
                        value={valueRequested}
                        onChange={(e) => setValueRequested(e.target.value)}
                      />
                    ) : (
                      <span className="font-bold text-slate-800 dark:text-slate-200 block py-1">
                        {valueRequested ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(valueRequested)) : '-'}
                      </span>
                    )}
                  </div>

                  {/* Valor Aprovado */}
                  <div>
                    <label className="block text-slate-400 mb-1 font-semibold font-mono">Valor Aprovado (R$)</label>
                    {isEditMode ? (
                      <input
                        type="number"
                        className="w-full bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-850 rounded-lg p-2.5 focus:outline-none text-slate-800 dark:text-slate-100"
                        value={valueApproved}
                        onChange={(e) => setValueApproved(e.target.value)}
                      />
                    ) : (
                      <span className="font-bold text-emerald-600 block py-1">
                        {valueApproved ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(valueApproved)) : 'Aguardando Liberação'}
                      </span>
                    )}
                  </div>
                </div>

                {/* Dados do Solicitante / Contato */}
                <div className="border-t pt-4 border-slate-100 dark:border-slate-850 space-y-3 text-left">
                  <h4 className="font-bold text-xs text-slate-500 font-mono">Vincular & Editar Solicitante</h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-slate-400 mb-1 font-medium">Nome Solicitante</label>
                      {isEditMode ? (
                        <input
                          type="text"
                          className="w-full bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-850 rounded-lg p-2 focus:outline-none"
                          value={contactName}
                          onChange={(e) => setContactName(e.target.value)}
                        />
                      ) : (
                        <span className="block py-0.5 font-semibold text-slate-700 dark:text-slate-300">
                          {contactName || 
                           (solicitanteName && solicitanteName.toLowerCase() !== 'não identificado' && solicitanteName.toLowerCase() !== 'nao identificado' ? solicitanteName : null) || 
                           demand?.orgaoEmissor || 
                           'Não identificado'}
                        </span>
                      )}
                    </div>

                    <div>
                      <label className="block text-slate-400 mb-1 font-medium">Responsável / Cargo</label>
                      {isEditMode ? (
                        <input
                          type="text"
                          className="w-full bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-850 rounded-lg p-2 focus:outline-none"
                          value={contactResponsible}
                          onChange={(e) => setContactResponsible(e.target.value)}
                        />
                      ) : (
                        <span className="block py-0.5 text-slate-600 dark:text-slate-400">{contactResponsible || 'Não Cadastrado'}</span>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-slate-400 mb-1 font-medium">CPF/CNPJ</label>
                      {isEditMode ? (
                        <input
                          type="text"
                          className="w-full bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-850 rounded-lg p-2 focus:outline-none"
                          value={contactCpfCnpj}
                          onChange={(e) => setContactCpfCnpj(e.target.value)}
                        />
                      ) : (
                        <span className="block py-0.5 text-slate-500 font-mono">{contactCpfCnpj || '-'}</span>
                      )}
                    </div>

                    <div>
                      <label className="block text-slate-400 mb-1 font-medium">Telefone</label>
                      {isEditMode ? (
                        <input
                          type="text"
                          className="w-full bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-850 rounded-lg p-2 focus:outline-none"
                          value={contactPhone}
                          onChange={(e) => setContactPhone(e.target.value)}
                        />
                      ) : (
                        <span className="block py-0.5 text-slate-500 font-mono">{contactPhone || '-'}</span>
                      )}
                    </div>

                    <div>
                      <label className="block text-slate-400 mb-1 font-medium">E-mail</label>
                      {isEditMode ? (
                        <input
                          type="email"
                          className="w-full bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-850 rounded-lg p-2 focus:outline-none"
                          value={contactEmail}
                          onChange={(e) => setContactEmail(e.target.value)}
                        />
                      ) : (
                        <span className="block py-0.5 text-slate-500 truncate">{contactEmail || '-'}</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Novos Metadados Extraídos (IA) */}
                <div className="border-t pt-4 border-slate-100 dark:border-slate-850 space-y-3 text-left bg-blue-50/50 dark:bg-blue-900/10 p-4 rounded-xl">
                  <h4 className="font-bold text-xs text-blue-500 dark:text-blue-400 font-mono flex items-center gap-2">
                    <Sparkles className="w-3.5 h-3.5" /> Detalhes Estruturais do Documento (IA)
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-slate-400 mb-1 font-medium text-[10px] uppercase tracking-wider">Órgão Emissor</label>
                      <span className="block py-0.5 font-semibold text-slate-700 dark:text-slate-300">{demand.orgaoEmissor || 'Não identificado'}</span>
                    </div>
                    <div>
                      <label className="block text-slate-400 mb-1 font-medium text-[10px] uppercase tracking-wider">Destinatário</label>
                      <span className="block py-0.5 font-semibold text-slate-700 dark:text-slate-300">{demand.destinatario || 'Não identificado'}</span>
                    </div>
                    <div>
                      <label className="block text-slate-400 mb-1 font-medium text-[10px] uppercase tracking-wider">Signatário (Assinatura)</label>
                      <span className="block py-0.5 font-semibold text-slate-700 dark:text-slate-300">
                        {demand.signatarioName || 'Não identificado'}
                        {demand.signatarioCargo ? <span className="text-slate-500 text-[10px] ml-1">({demand.signatarioCargo})</span> : ''}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Remetente WhatsApp */}
                {(demand.senderPhone || demand.senderName) && (
                  <div className="border-t pt-4 border-slate-100 dark:border-slate-850 space-y-3 text-left">
                    <h4 className="font-bold text-xs text-emerald-600 dark:text-emerald-500 font-mono flex items-center gap-2">
                      <Zap className="w-3.5 h-3.5" /> Remetente da Mensagem (WhatsApp)
                    </h4>
                    <div className="flex items-center gap-4 bg-emerald-50 dark:bg-emerald-900/10 p-3 rounded-lg border border-emerald-100 dark:border-emerald-800/30">
                      <div>
                        <label className="block text-emerald-600/70 dark:text-emerald-500/70 mb-0.5 font-medium text-[10px] uppercase tracking-wider">Nome no Perfil</label>
                        <span className="block font-semibold text-emerald-800 dark:text-emerald-300">{demand.senderName || 'Não informado'}</span>
                      </div>
                      <div className="w-px h-8 bg-emerald-200 dark:bg-emerald-800/50"></div>
                      <div>
                        <label className="block text-emerald-600/70 dark:text-emerald-500/70 mb-0.5 font-medium text-[10px] uppercase tracking-wider">
                          {demand.senderPhone ? 'Telefone Origem' : (demand.contact?.phone ? 'Telefone do Cadastro (IA)' : 'Telefone Origem')}
                        </label>
                        <span className="block font-mono text-emerald-800 dark:text-emerald-300">
                          {demand.senderPhone || demand.contact?.phone || (demand.contact?.lid ? 'LID (Pendente)' : 'Não informado')}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Histórico de Comunicações */}
                <div className="border-t pt-4 border-slate-100 dark:border-slate-850 space-y-3 text-left">
                  <h4 className="font-bold text-xs text-slate-500 font-mono flex items-center gap-2">
                    <Mail className="w-3.5 h-3.5" /> Histórico de Comunicações
                  </h4>
                  {messagesList.length === 0 ? (
                    <p className="text-sm text-slate-500 italic">Nenhuma comunicação enviada/recebida para esta demanda ainda.</p>
                  ) : (
                    <div className="space-y-3 mt-2">
                      {messagesList.slice(0, showAllCommunications ? messagesList.length : 2).map((msg: any) => (
                        <div key={msg.id} className="p-3 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 animate-in fade-in duration-200">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              {msg.channel === 'WHATSAPP' ? <Smartphone className="w-4 h-4 text-emerald-500" /> : <Mail className="w-4 h-4 text-blue-500" />}
                              <span className="font-bold text-xs text-slate-700 dark:text-slate-300">
                                {msg.channel === 'WHATSAPP' ? 'WhatsApp' : 'E-mail'} {msg.direction === 'OUTGOING' ? 'Enviado' : 'Recebido'}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-400 px-2 py-0.5 rounded font-mono">
                                {new Date(msg.createdAt).toLocaleString('pt-BR')}
                              </span>
                            </div>
                          </div>
                          <div className="text-xs text-slate-500 mb-1">
                            <span className="font-semibold">Destino/Origem:</span> {msg.senderAddress}
                          </div>
                          <div className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap bg-white dark:bg-slate-950 p-3 rounded-lg border border-slate-100 dark:border-slate-800/60 font-serif">
                            {msg.text}
                          </div>
                        </div>
                      ))}
                      
                      {messagesList.length > 2 && (
                        <button
                          type="button"
                          onClick={() => setShowAllCommunications(!showAllCommunications)}
                          className="w-full text-center py-2 border border-dashed border-slate-250 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-900/60 text-slate-500 rounded-xl transition-all font-semibold text-xs mt-2"
                        >
                          {showAllCommunications ? 'Ver Menos Comunicações' : `Ver Mais (${messagesList.length - 2} ocultas)`}
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Campo: Resumo */}
                <div className="border-t pt-4 border-slate-100 dark:border-slate-850">
                  <label className="block text-slate-400 mb-1 font-semibold font-mono">Resumo Executivo (IA)</label>
                  {isEditMode ? (
                    <textarea
                      rows={3}
                      className="w-full bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-850 rounded-lg p-2.5 focus:outline-none text-slate-700 dark:text-slate-200 resize-none font-sans"
                      value={summary}
                      onChange={(e) => setSummary(e.target.value)}
                    />
                  ) : (
                    <p className="text-slate-650 dark:text-slate-300 leading-relaxed bg-slate-50 dark:bg-slate-950 p-4 rounded-xl border border-slate-100 dark:border-slate-900/60 font-sans">
                      {summary}
                    </p>
                  )}
                </div>

              </div>

              {/* Botões Salvar Manual */}
              {isEditMode && !autosave && (
                <div className="pt-2 flex justify-end gap-2">
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl"
                  >
                    Salvar Alterações
                  </button>
                  <button
                    onClick={() => {
                      setIsEditMode(false);
                      loadDemand();
                    }}
                    className="px-4 py-2 bg-slate-100 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-500 rounded-xl"
                  >
                    Cancelar
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ABA: ENCAMINHAMENTOS */}
          {activeSubTab === 'forwardings' && (
            <div className="glass-panel p-6 rounded-3xl space-y-4 border border-slate-200 dark:border-slate-800 text-xs text-left animate-in fade-in duration-200">
              <h2 className="font-bold text-sm font-sans flex items-center gap-1.5 border-b pb-2 border-slate-100 dark:border-slate-850">
                <Landmark className="w-4 h-4 text-blue-500" />
                Registrar Encaminhamento de Emenda / Solicitação
              </h2>

              {/* Novo Encaminhamento */}
              {userPermissions.includes('demand:edit') && (
                <form onSubmit={handleAddForwarding} className="space-y-4 bg-slate-50 dark:bg-slate-950 p-4 rounded-2xl border border-slate-100 dark:border-slate-900">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-slate-400 mb-1 font-medium">Órgão Público Destinatário</label>
                      <input
                        type="text"
                        required
                        placeholder="Ex: Secretaria da Saúde - SP"
                        className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-2 focus:outline-none"
                        value={forwardingAgency}
                        onChange={(e) => setForwardingAgency(e.target.value)}
                      />
                    </div>

                    <div>
                      <label className="block text-slate-400 mb-1 font-medium">Responsável no Órgão</label>
                      <input
                        type="text"
                        placeholder="Ex: João Silva (Diretor Técnico)"
                        className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-2 focus:outline-none"
                        value={forwardingResponsible}
                        onChange={(e) => setForwardingResponsible(e.target.value)}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-slate-400 mb-1 font-medium">Observações do Encaminhamento</label>
                    <textarea
                      rows={2}
                      placeholder="Anote detalhes de ofício entregue ou data de tratativa..."
                      className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-2 focus:outline-none font-sans resize-none"
                      value={forwardingNotes}
                      onChange={(e) => setForwardingNotes(e.target.value)}
                    />
                  </div>

                  <button
                    type="submit"
                    className="py-2 px-4 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl transition-all"
                  >
                    Registrar Encaminhamento
                  </button>
                </form>
              )}

              {/* Lista de Encaminhamentos (Puxada do histórico de status) */}
              <div className="space-y-3">
                <h3 className="font-bold font-mono text-slate-500 text-[10px] uppercase">Histórico de Encaminhamentos</h3>
                {demand.statusHistories?.filter(h => h.statusTo === 'ENCAMINHADA').length === 0 ? (
                  <div className="py-4 text-center text-slate-400 italic">Nenhum encaminhamento registrado para esta demanda.</div>
                ) : (
                  demand.statusHistories?.filter(h => h.statusTo === 'ENCAMINHADA').map((item, idx) => (
                    <div key={idx} className="p-3 border border-slate-100 dark:border-slate-850 rounded-xl flex justify-between gap-4 bg-slate-50/50 dark:bg-slate-900/10">
                      <div className="space-y-1">
                        <strong className="block text-slate-800 dark:text-slate-200">
                          {item.notes?.includes('|') ? item.notes.split('|')[0].replace('Órgão:', '').trim() : 'Órgão de Destino'}
                        </strong>
                        <span className="block text-slate-500 text-[11px]">
                          {item.notes?.includes('|') ? item.notes.split('|')[1].trim() : ''}
                        </span>
                        <p className="text-[11px] text-slate-400 italic">
                          {item.notes?.includes('|') ? item.notes.split('|')[2].replace('Obs:', '').trim() : item.notes}
                        </p>
                      </div>
                      <span className="text-[10px] text-slate-400 font-mono text-right shrink-0">
                        {new Date(item.createdAt).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* ABA: DOCUMENTOS GERADOS */}
          {activeSubTab === 'documents' && (
            <div className="glass-panel p-6 rounded-3xl space-y-4 border border-slate-200 dark:border-slate-800 text-xs text-left animate-in fade-in duration-200">
              <h2 className="font-bold text-sm font-sans flex items-center gap-1.5 border-b pb-2 border-slate-100 dark:border-slate-850">
                <FileText className="w-4 h-4 text-blue-500" />
                Documentos Oficiais Gerados (Ofícios)
              </h2>

              <div className="overflow-x-auto">
                {demand.generatedDocuments && demand.generatedDocuments.length > 0 ? (
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-slate-100 dark:border-slate-800 text-slate-400 font-mono uppercase text-[9px]">
                        <th className="py-2.5 font-bold">Versão</th>
                        <th className="py-2.5 font-bold">Template</th>
                        <th className="py-2.5 font-bold">Data</th>
                        <th className="py-2.5 font-bold">Usuário</th>
                        <th className="py-2.5 font-bold">Status</th>
                        <th className="py-2.5 font-bold text-right">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-slate-700 dark:text-slate-350">
                      {demand.generatedDocuments.map(doc => (
                        <tr key={doc.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-900/40 transition-colors">
                          <td className="py-3 font-mono font-bold">
                            <span className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-[10px]">
                              v{doc.version}
                            </span>
                          </td>
                          <td className="py-3 font-medium">
                            <span className="px-2 py-0.5 rounded bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-[10px] font-bold">
                              {doc.templateCode || doc.templateType || 'GABINETE'}
                            </span>
                          </td>
                          <td className="py-3 text-slate-500">
                            {new Date(doc.createdAt).toLocaleDateString('pt-BR')} {new Date(doc.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                          </td>
                          <td className="py-3 font-medium text-slate-600 dark:text-slate-300">
                            {doc.createdBy?.name || 'Sistema'}
                          </td>
                          <td className="py-3">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                              doc.status === 'SENT' ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400' :
                              doc.status === 'FAILED' ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400' :
                              'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400'
                            }`}>
                              {doc.status}
                            </span>
                          </td>
                          <td className="py-3 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              <a 
                                href={doc.fileUrl.startsWith('/uploads/') ? `/api${doc.fileUrl}` : doc.fileUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="p-1.5 text-slate-400 hover:text-blue-500 bg-slate-50 dark:bg-slate-800/50 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-all"
                                title="Visualizar Documento"
                              >
                                <Eye className="w-3.5 h-3.5" />
                              </a>
                              <a 
                                href={doc.fileUrl.startsWith('/uploads/') ? `/api${doc.fileUrl}` : doc.fileUrl} 
                                download={doc.fileName}
                                className="p-1.5 text-slate-400 hover:text-emerald-500 bg-slate-50 dark:bg-slate-800/50 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded-lg transition-all"
                                title="Baixar PDF"
                              >
                                <Download className="w-3.5 h-3.5" />
                              </a>
                              {userPermissions.includes('demand:edit') && (
                                <button 
                                  onClick={() => {
                                    const defaultPhone = demand.contact?.phone || demand.senderPhone || '';
                                    const defaultEmail = demand.contact?.email || '';
                                    setResendDocId(doc.id);
                                    setResendPhone(defaultPhone);
                                    setResendEmailAddress(defaultEmail);
                                    setResendChannels({ whatsapp: true, email: false });
                                    setIsResendModalOpen(true);
                                  }}
                                  className="p-1.5 text-slate-400 hover:text-amber-500 bg-slate-50 dark:bg-slate-800/50 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded-lg transition-all"
                                  title="Reenviar Documento"
                                >
                                  <Send className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center py-6 text-slate-400 font-medium">Nenhum documento oficial gerado para esta demanda.</div>
                )}
              </div>
            </div>
          )}

          {/* ABA: ANEXOS */}
          {activeSubTab === 'attachments' && (
            <div className="glass-panel p-6 rounded-3xl space-y-4 border border-slate-200 dark:border-slate-800 text-xs text-left animate-in fade-in duration-200">
              <h2 className="font-bold text-sm font-sans flex items-center gap-1.5 border-b pb-2 border-slate-100 dark:border-slate-850">
                <FileSpreadsheet className="w-4 h-4 text-blue-500" />
                Anexos da Demanda (PDF, Imagens, Word)
              </h2>

              {/* Novo Anexo */}
              {userPermissions.includes('demand:edit') && (
                <form onSubmit={handleAddAttachment} className="flex flex-wrap items-end gap-3 bg-slate-50 dark:bg-slate-950 p-4 rounded-2xl border border-slate-100 dark:border-slate-900">
                  <div className="flex-1 min-w-[150px]">
                    <label className="block text-slate-400 mb-1 font-medium">Nome do Arquivo (Opcional)</label>
                    <input
                      type="text"
                      placeholder="Ex: Ofício Santa Casa Assinado.pdf"
                      className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-2 focus:outline-none"
                      value={newAttachmentName}
                      onChange={(e) => setNewAttachmentName(e.target.value)}
                    />
                  </div>

                  <div className="flex-1 min-w-[150px]">
                    <label className="block text-slate-400 mb-1 font-medium">Selecionar Arquivo</label>
                    <input
                      type="file"
                      required
                      className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-1.5 focus:outline-none"
                      onChange={(e) => {
                        if (e.target.files && e.target.files.length > 0) {
                          setNewAttachmentFile(e.target.files[0]);
                          if (!newAttachmentName) {
                            setNewAttachmentName(e.target.files[0].name);
                          }
                        }
                      }}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={!newAttachmentFile}
                    className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg font-bold transition-all"
                  >
                    Anexar
                  </button>
                </form>
              )}

              {/* Lista de Anexos */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {demand.attachments && demand.attachments.length > 0 ? (
                  demand.attachments.map(att => (
                    <div key={att.id} className="p-3 border border-slate-100 dark:border-slate-850 rounded-xl flex items-center justify-between gap-3 bg-slate-50/50 dark:bg-slate-900/10">
                      <div className="flex items-center gap-2 truncate">
                        <FileText className="w-5 h-5 text-slate-450 shrink-0" />
                        <div className="truncate">
                          <strong className="block truncate text-slate-850 dark:text-slate-200">{att.fileName}</strong>
                          <span className="text-[10px] text-slate-400">{att.fileType.split('/')[1].toUpperCase()}</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-1 shrink-0">
                        {/* Baixar */}
                        <button
                          type="button"
                          onClick={() => handleDownloadAttachment(att.fileUrl, att.fileName)}
                          className="p-1.5 hover:bg-slate-105 dark:hover:bg-slate-800 rounded text-slate-550 hover:text-blue-500 transition-all cursor-pointer"
                          title="Baixar/Visualizar Anexo"
                        >
                          <Download className="w-4 h-4" />
                        </button>

                        {/* Remover */}
                        {userPermissions.includes('demand:edit') && (
                          <button
                            type="button"
                            onClick={() => handleRemoveAttachment(att.id)}
                            className="p-1.5 hover:bg-red-500/15 rounded text-slate-400 hover:text-red-500 transition-all"
                            title="Remover Anexo"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="col-span-2 py-8 text-center text-slate-400 italic">Nenhum anexo registrado.</div>
                )}
              </div>
            </div>
          )}

          {/* ABA: COMENTÁRIOS INTERNOS (DISCUSSÃO EQUIPE) */}
          {activeSubTab === 'comments' && (
            <div className="glass-panel p-6 rounded-3xl space-y-4 border border-slate-200 dark:border-slate-800 text-xs text-left animate-in fade-in duration-200">
              <div className="flex justify-between items-center border-b pb-2 border-slate-100 dark:border-slate-850">
                <h2 className="font-bold text-sm font-sans flex items-center gap-1.5">
                  <ShieldAlert className="w-4 h-4 text-amber-500" />
                  Discussão Interna / Comentários Privados
                </h2>
                <span className="flex items-center gap-0.5 px-2 py-0.5 bg-slate-100 dark:bg-slate-800 border rounded text-[9px] text-slate-400 font-mono uppercase">
                  <EyeOff className="w-2.5 h-2.5" />
                  Oculto Público
                </span>
              </div>

              {/* Feed de Comentários */}
              <div className="space-y-3 max-h-60 overflow-y-auto pr-1">
                {commentsList.map((c: any, index: number) => (
                  <div key={c.id || index} className="p-3 border border-slate-100 dark:border-slate-850 rounded-xl bg-slate-50/50 dark:bg-slate-900/40 flex flex-col gap-1">
                    <div className="flex flex-col sm:flex-row sm:justify-between gap-0.5 font-bold text-slate-550 dark:text-slate-400 text-[10px]">
                      <span className="truncate text-slate-650 dark:text-slate-350 font-semibold">{c.user?.name || 'Assessor'} ({c.user?.email || 'gabinete@gabinete.com'})</span>
                      <span className="font-mono font-normal shrink-0 text-slate-450 dark:text-slate-450">{c.createdAt ? new Date(c.createdAt).toLocaleString('pt-BR') : ''}</span>
                    </div>
                    <p className="text-slate-700 dark:text-slate-200 leading-normal">{c.text}</p>
                  </div>
                ))}
              </div>

              {/* Formulário Novo Comentário */}
              {userPermissions.includes('demand:edit') && (
                <form onSubmit={handleAddComment} className="flex gap-2">
                  <input
                    type="text"
                    required
                    placeholder="Digite um comentário ou atualização para a equipe..."
                    className="flex-1 bg-slate-100 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none"
                    value={newCommentText}
                    onChange={(e) => setNewCommentText(e.target.value)}
                  />
                  <button
                    type="submit"
                    className="px-4 bg-slate-900 dark:bg-slate-100 hover:bg-slate-800 dark:hover:bg-white text-white dark:text-slate-950 font-bold rounded-xl transition-all"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </form>
              )}
            </div>
          )}

          {/* ABA: TIMELINE UNIFICADA */}
          {activeSubTab === 'timeline' && (
            <div className="glass-panel p-6 rounded-3xl space-y-4 border border-slate-200 dark:border-slate-800 text-xs text-left animate-in fade-in duration-200">
              <h2 className="font-bold text-sm font-sans flex items-center gap-1.5 border-b pb-2 border-slate-100 dark:border-slate-850">
                <Clock className="w-4 h-4 text-blue-500" />
                Histórico Operacional & Timeline da Demanda
              </h2>

              <div className="relative border-l-2 border-slate-200 dark:border-slate-800 ml-3 pl-5 space-y-6">
                {unifiedTimelineEvents.map(event => (
                  <div key={event.id} className="relative">
                    {/* Node Dot */}
                    <div className={`absolute left-[-26px] top-1.5 w-3 h-3 rounded-full ring-4 ring-white dark:ring-slate-950 ${
                      event.type === 'status' ? 'bg-indigo-500' : (event.type === 'audit' ? 'bg-amber-500' : 'bg-blue-500')
                    }`}></div>

                    <div className="space-y-0.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <strong className="text-slate-850 dark:text-slate-100 text-xs">
                          {event.title}
                        </strong>
                        <span className="text-[10px] text-slate-400 font-mono">
                          {new Date(event.date).toLocaleDateString('pt-BR')} às {new Date(event.date).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <p className="text-slate-500 dark:text-slate-400 leading-relaxed text-[11px]">
                        {event.description}
                      </p>
                      <span className="text-[10px] text-slate-400 block font-semibold">
                        Ação por: {event.user}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* 4. MODAL: REGRA DE TREINAMENTO (IA CORRIGIR) */}
      {isAICorrectOpen && (
        <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl w-full max-w-sm p-6 shadow-2xl relative text-left">
            <button
              onClick={() => setIsAICorrectOpen(false)}
              className="absolute right-4 top-4 p-1 hover:bg-slate-105 dark:hover:bg-slate-800 rounded text-slate-400"
            >
              <X className="w-5 h-5" />
            </button>

            <h3 className="font-bold text-base mb-2 flex items-center gap-1">
              <Zap className="w-4 h-4 text-blue-500" />
              Mecanismo Dicionário IA
            </h3>
            <p className="text-[11px] text-slate-500 mb-4">Adicione uma regra de classificação forçada para que a IA classifique corretamente os próximos ofícios contendo esse termo.</p>

            <form onSubmit={handleSaveAICorrection} className="space-y-4 text-xs">
              <div>
                <label className="block text-slate-400 mb-1 font-medium">Termo / Palavra-Chave</label>
                <input
                  type="text"
                  required
                  placeholder="Ex: UBS, SCFV, CDs"
                  className="w-full bg-slate-100 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none"
                  value={aiCorrectTerm}
                  onChange={(e) => setAiCorrectTerm(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-slate-400 mb-1 font-medium">Área Mapeada Correspondente</label>
                <select
                  className="w-full bg-slate-100 dark:bg-slate-950 border border-slate-200 dark:border-slate-900 rounded-xl p-2.5 focus:outline-none"
                  value={aiCorrectArea}
                  onChange={(e) => setAiCorrectArea(e.target.value)}
                >
                  <option value="Saúde">Saúde</option>
                  <option value="Educação">Educação</option>
                  <option value="Infraestrutura">Infraestrutura</option>
                  <option value="Segurança Pública">Segurança Pública</option>
                  <option value="Assistência Social">Assistência Social</option>
                  <option value="Habitação">Habitação</option>
                </select>
              </div>

              <div className="pt-2 flex gap-2">
                <button
                  type="submit"
                  className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-550 text-white font-bold rounded-xl"
                >
                  Salvar Regra
                </button>
                <button
                  type="button"
                  onClick={() => setIsAICorrectOpen(false)}
                  className="px-4 py-2.5 bg-slate-100 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-500 rounded-xl"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 5. MODAL: REPROCESSAR COM IA */}
      {isReprocessOpen && (
        <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl w-full max-w-md p-6 shadow-2xl relative text-left">
            <button
              onClick={() => { setIsReprocessOpen(false); setAiSuggestions(null); }}
              className="absolute right-4 top-4 p-1 hover:bg-slate-105 dark:hover:bg-slate-800 rounded text-slate-400"
            >
              <X className="w-5 h-5" />
            </button>

            <h3 className="font-bold text-base mb-2 flex items-center gap-1.5">
              <Sparkles className="w-5 h-5 text-purple-500" />
              Reprocessar Ofício com IA
            </h3>
            <p className="text-[11px] text-slate-500 mb-4">A IA executará uma releitura OCR robusta e detalhamento do arquivo timbrado original da demanda.</p>

            <div className="space-y-4 text-xs">
              <div className="flex gap-4">
                <label className="flex items-center gap-1.5 cursor-pointer font-semibold">
                  <input
                    type="radio"
                    name="reprocessMode"
                    value="suggest"
                    checked={reprocessMode === 'suggest'}
                    onChange={() => { setReprocessMode('suggest'); setAiSuggestions(null); }}
                  />
                  Apenas sugerir novos dados
                </label>

                <label className="flex items-center gap-1.5 cursor-pointer font-semibold">
                  <input
                    type="radio"
                    name="reprocessMode"
                    value="replace"
                    checked={reprocessMode === 'replace'}
                    onChange={() => { setReprocessMode('replace'); setAiSuggestions(null); }}
                  />
                  Substituir dados automaticamente
                </label>
              </div>

              {/* Bloco de sugestão exibido após clicar */}
              {aiSuggestions && (
                <div className="bg-purple-500/5 p-4 border border-purple-500/10 rounded-2xl space-y-3 max-h-60 overflow-y-auto">
                  <span className="font-bold text-purple-500 text-[10px] uppercase font-mono block">Sugestões de dados extraídos pela IA:</span>
                  
                  {Object.keys(aiSuggestions).map((key) => (
                    <div key={key} className="p-2 bg-white dark:bg-slate-900 border rounded-lg flex items-center justify-between gap-4">
                      <div>
                        <strong className="block text-[10px] text-slate-400 font-mono capitalize">{key}</strong>
                        <span className="text-[11px] font-semibold text-slate-700 dark:text-slate-200">
                          {key === 'valueRequested' ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(aiSuggestions[key]) : aiSuggestions[key]}
                        </span>
                      </div>
                      <button
                        onClick={() => acceptSuggestion(key, aiSuggestions[key])}
                        className="px-2 py-1 bg-purple-600 hover:bg-purple-500 text-white font-bold rounded text-[9px]"
                      >
                        Aceitar
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="pt-2 flex gap-2">
                <button
                  onClick={handleReprocessIA}
                  className="flex-1 py-2.5 bg-purple-600 hover:bg-purple-550 text-white font-bold rounded-xl"
                >
                  Disparar IA GPT-4o
                </button>
                <button
                  onClick={() => { setIsReprocessOpen(false); setAiSuggestions(null); }}
                  className="px-4 py-2.5 bg-slate-100 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-500 rounded-xl"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>

    {/* Mobile sticky action bar */}
    <div className="mobile-action-bar md:hidden flex items-center justify-between gap-2 px-4 py-3 bg-white dark:bg-slate-950 border-t border-slate-200 dark:border-slate-800">
      <div className="flex items-center gap-2">
        <button onClick={handleToggleFavorite} className="p-3 bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-500">
          <Star className={`w-5 h-5 ${demand.isFavorite ? 'fill-amber-500 text-amber-500' : ''}`} />
        </button>
        <button onClick={handlePrint} className="p-3 bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-500">
          <Printer className="w-5 h-5" />
        </button>
        {userPermissions.includes('demand:edit') && (
           <button onClick={() => setIsReprocessOpen(true)} className="p-3 bg-purple-100 dark:bg-purple-900/30 text-purple-600 rounded-xl border border-purple-200 dark:border-purple-800/50">
             <Sparkles className="w-5 h-5" />
           </button>
        )}
      </div>
      <div className="flex items-center gap-2 flex-1 justify-end">
        {isEditMode ? (
          <button type="button" onClick={handleSave} className="flex-1 max-w-[140px] py-3 rounded-xl bg-emerald-600 text-white font-bold text-sm shadow-lg shadow-emerald-500/20">Salvar</button>
        ) : (
          <>
            {userPermissions.includes('demand:edit') && demand.status !== 'APROVADA' && (
              <button onClick={() => setIsApprovalModalOpen(true)} className="flex-1 py-3 rounded-xl bg-emerald-600 text-white font-bold text-sm shadow-lg shadow-emerald-500/20 flex items-center justify-center gap-1">
                <CheckCircle2 className="w-4 h-4"/> Aprovar
              </button>
            )}
            {userPermissions.includes('demand:edit') && (
              <button onClick={() => setIsEditMode(true)} className="flex-1 max-w-[140px] py-3 rounded-xl bg-blue-600 text-white font-bold text-sm shadow-lg shadow-blue-500/20">Editar</button>
            )}
          </>
        )}
      </div>
    </div>

    {/* Modal de Aprovação */}
    {isApprovalModalOpen && (
      <ApprovalModal
        demand={demand}
        onClose={() => setIsApprovalModalOpen(false)}
        onConfirm={handleApproval}
      />
    )}

    {/* Modal de Ajuste de Relacionamento CRM */}
    {isResolveModalOpen && (
      <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm transition-opacity">
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-2xl p-6 w-full max-w-md animate-in fade-in zoom-in-95 duration-200 text-left">
          <div className="flex items-start gap-3 mb-4">
            <div className="p-3 bg-purple-50 dark:bg-purple-900/30 text-purple-500 rounded-2xl shrink-0">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-800 dark:text-slate-100">
                Ajustar Vínculos CRM
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
                Selecione as entidades corretas no CRM para vincular a esta Demanda.
              </p>
            </div>
          </div>

          <div className="space-y-4 mb-5 text-xs">
            {/* Selecionar Município */}
            <div>
              <label className="block text-slate-400 mb-1 font-semibold font-mono">Município CRM</label>
              <select
                value={selectedResolveMunicipalityId}
                onChange={(e) => setSelectedResolveMunicipalityId(e.target.value)}
                className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-850 rounded-xl py-2.5 px-3 text-slate-850 dark:text-slate-100 focus:outline-none"
              >
                <option value="">Nenhum</option>
                {municipalities.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name} - {m.state}
                  </option>
                ))}
              </select>
            </div>

            {/* Selecionar Entidade */}
            <div>
              <label className="block text-slate-400 mb-1 font-semibold font-mono">Entidade CRM</label>
              <select
                value={selectedResolveEntityId}
                onChange={(e) => setSelectedResolveEntityId(e.target.value)}
                className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-850 rounded-xl py-2.5 px-3 text-slate-850 dark:text-slate-100 focus:outline-none"
              >
                <option value="">Nenhuma</option>
                {entities
                  .filter((e) => !selectedResolveMunicipalityId || e.municipalityId === selectedResolveMunicipalityId)
                  .map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.name} ({e.type})
                    </option>
                  ))}
              </select>
            </div>

            {/* Selecionar Pessoa */}
            <div>
              <label className="block text-slate-400 mb-1 font-semibold font-mono">Gestor / Representante CRM</label>
              <select
                value={selectedResolvePersonId}
                onChange={(e) => setSelectedResolvePersonId(e.target.value)}
                className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-205 dark:border-slate-850 rounded-xl py-2.5 px-3 text-slate-850 dark:text-slate-100 focus:outline-none"
              >
                <option value="">Nenhum</option>
                {people
                  .filter((p) => !selectedResolveMunicipalityId || p.municipalityId === selectedResolveMunicipalityId)
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} - {p.role}
                    </option>
                  ))}
              </select>
            </div>

            {/* Motivo do Ajuste */}
            <div>
              <label className="block text-slate-400 mb-1 font-semibold font-mono">Motivo / Justificativa</label>
              <input
                type="text"
                placeholder="Ex: Corrigido após verificar nome do remetente."
                value={resolveReason}
                onChange={(e) => setResolveReason(e.target.value)}
                className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-205 dark:border-slate-850 rounded-xl py-2.5 px-3 text-slate-850 dark:text-slate-100 focus:outline-none"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 text-xs">
            <button
              onClick={() => setIsResolveModalOpen(false)}
              className="px-4 py-2 font-semibold text-slate-500 dark:text-slate-400 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700/80 rounded-xl transition-all"
            >
              Cancelar
            </button>
            <button
              onClick={handleSaveResolveRelationship}
              className="px-4 py-2 font-semibold text-white bg-blue-600 hover:bg-blue-750 rounded-xl transition-all shadow-lg shadow-blue-500/10"
            >
              Confirmar Ajuste
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Modal Customizado para Reenviar Documento (WhatsApp / Email) */}
    {isResendModalOpen && (
      <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm transition-opacity">
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-2xl p-6 w-full max-w-md animate-in fade-in zoom-in-95 duration-200 text-left">
          <div className="flex items-start gap-3 mb-4">
            <div className="p-3 bg-blue-50 dark:bg-blue-900/30 text-blue-500 rounded-2xl shrink-0">
              <Send className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-800 dark:text-slate-100">
                Reenviar Documento Oficial
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
                Selecione os canais de comunicação e preencha os dados de destino para reenviar a via gerada.
              </p>
            </div>
          </div>

          <div className="space-y-4 mb-5">
            {/* Opção WhatsApp */}
            <div className="space-y-2 border border-slate-100 dark:border-slate-800/80 p-3 rounded-2xl">
              <label className="flex items-center gap-2 cursor-pointer font-bold text-xs text-slate-700 dark:text-slate-200">
                <input 
                  type="checkbox" 
                  checked={resendChannels.whatsapp} 
                  onChange={(e) => setResendChannels({ ...resendChannels, whatsapp: e.target.checked })}
                  className="rounded border-slate-350 text-blue-600 focus:ring-blue-500" 
                />
                Enviar por WhatsApp
              </label>
              {resendChannels.whatsapp && (
                <input
                  type="text"
                  placeholder="Número com DDD (ex: 1438461127)"
                  value={resendPhone}
                  onChange={(e) => setResendPhone(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl py-2 px-3 text-xs text-slate-805 dark:text-slate-100 focus:outline-none focus:border-blue-500 font-medium"
                />
              )}
            </div>

            {/* Opção E-mail */}
            <div className="space-y-2 border border-slate-100 dark:border-slate-800/80 p-3 rounded-2xl">
              <label className="flex items-center gap-2 cursor-pointer font-bold text-xs text-slate-700 dark:text-slate-200">
                <input 
                  type="checkbox" 
                  checked={resendChannels.email} 
                  onChange={(e) => setResendChannels({ ...resendChannels, email: e.target.checked })}
                  className="rounded border-slate-350 text-blue-600 focus:ring-blue-500" 
                />
                Enviar por E-mail
              </label>
              {resendChannels.email && (
                <input
                  type="email"
                  placeholder="Endereço de e-mail"
                  value={resendEmailAddress}
                  onChange={(e) => setResendEmailAddress(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl py-2 px-3 text-xs text-slate-805 dark:text-slate-100 focus:outline-none focus:border-blue-500 font-medium"
                />
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button
              onClick={() => setIsResendModalOpen(false)}
              className="px-4 py-2 text-xs font-semibold text-slate-500 dark:text-slate-400 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700/80 rounded-xl transition-all"
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirmResend}
              className="px-4 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-750 rounded-xl transition-all shadow-lg shadow-blue-500/10"
            >
              Confirmar Envio
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default DemandDetail;
